import { z } from "zod";

import { FollowUpPolicyEnum } from "@/lib/enums/follow-up-policy-enum";
import { getTotalMinutes } from "@/lib/time";

export const FIRST_APPOINTMENT_STRATEGIES = [
  "attract",
  "average",
  "premium",
  "custom",
] as const;

export const FOLLOW_UP_DAYS_OPTIONS = ["15", "30", "60"] as const;

export const FOLLOW_UP_CUSTOM_DAYS_OPTION = "other";

export const firstAppointmentDraftSchema = z.object({
  strategy: z.enum(FIRST_APPOINTMENT_STRATEGIES),
  price: z.number().positive(),
  duration: z.string(),
});

export const followUpDraftSchema = z.object({
  policy: z.enum(FollowUpPolicyEnum),
  daysToFreeFollowUp: z.number().int().min(1).max(999).nullable(),
  price: z.number().min(0),
  duration: z.string(),
});

export type FirstAppointmentDraft = z.infer<typeof firstAppointmentDraftSchema>;
export type FollowUpDraft = z.infer<typeof followUpDraftSchema>;

interface DurationLimits {
  minDurationInMinutes: number;
  maxDurationInMinutes: number;
}

export const validateAppointmentDuration = (
  duration: string,
  { minDurationInMinutes, maxDurationInMinutes }: DurationLimits,
): string | null => {
  const totalMinutes = getTotalMinutes(duration);

  if (totalMinutes < minDurationInMinutes) {
    return `A duração mínima é de ${minDurationInMinutes} minutos`;
  }

  if (totalMinutes > maxDurationInMinutes) {
    return `A duração máxima é de ${maxDurationInMinutes / 60} horas`;
  }

  return null;
};

export const parseMaskedPrice = (maskedPrice: string | undefined): number =>
  Number((maskedPrice ?? "").replace(/\D/g, "")) / 100;

export const buildFirstAppointmentFormSchema = (limits: DurationLimits) =>
  z
    .object({
      strategy: z.enum(FIRST_APPOINTMENT_STRATEGIES, { error: "Escolha um valor" }),
      customPrice: z.string().optional(),
      duration: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.strategy === "custom" && parseMaskedPrice(values.customPrice) <= 0) {
        ctx.addIssue({
          code: "custom",
          path: ["customPrice"],
          message: "Informe o valor da consulta",
        });
      }

      const durationError = validateAppointmentDuration(values.duration, limits);
      if (durationError) {
        ctx.addIssue({ code: "custom", path: ["duration"], message: durationError });
      }
    });

export type FirstAppointmentFormFields = z.infer<
  ReturnType<typeof buildFirstAppointmentFormSchema>
>;

export const isFreeFollowUpPolicy = (policy: FollowUpPolicyEnum | undefined): boolean =>
  policy === FollowUpPolicyEnum.FreeFollowUp || policy === FollowUpPolicyEnum.CaseByCase;

export const showsFollowUpDuration = (policy: FollowUpPolicyEnum | undefined): boolean =>
  policy === FollowUpPolicyEnum.NoFreeFollowUp ||
  policy === FollowUpPolicyEnum.CaseByCase;

export const buildFollowUpFormSchema = (limits: DurationLimits) =>
  z
    .object({
      policy: z.enum(FollowUpPolicyEnum, { error: "Escolha como funciona o retorno" }),
      price: z.string().optional(),
      duration: z.string(),
    })
    .superRefine((values, ctx) => {
      if (
        values.policy === FollowUpPolicyEnum.NoFreeFollowUp &&
        parseMaskedPrice(values.price) <= 0
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["price"],
          message: "Informe o valor do retorno",
        });
      }

      if (showsFollowUpDuration(values.policy)) {
        const durationError = validateAppointmentDuration(values.duration, limits);
        if (durationError) {
          ctx.addIssue({ code: "custom", path: ["duration"], message: durationError });
        }
      }
    });

export type FollowUpFormFields = z.infer<ReturnType<typeof buildFollowUpFormSchema>>;

export const followUpDaysFormSchema = z
  .object({
    daysOption: z.string({ error: "Informe em até quantos dias" }),
    customDays: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.daysOption !== FOLLOW_UP_CUSTOM_DAYS_OPTION) return;

    const days = Number(values.customDays);
    if (!Number.isInteger(days) || days < 1 || days > 999) {
      ctx.addIssue({
        code: "custom",
        path: ["customDays"],
        message: "A quantidade de dias deve estar entre 1 e 999",
      });
    }
  });

export type FollowUpDaysFormFields = z.infer<typeof followUpDaysFormSchema>;

export const toFollowUpDays = ({
  daysOption,
  customDays,
}: FollowUpDaysFormFields): number =>
  daysOption === FOLLOW_UP_CUSTOM_DAYS_OPTION ? Number(customDays) : Number(daysOption);
